import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import Quiz from '../components/Quiz'
import Specimen from '../components/Specimen'
import TrafficPanel from '../components/TrafficPanel'
import Markdown from '../components/Markdown'
import NoteEditor from '../components/NoteEditor'

interface LessonPayload {
  lesson: {
    id: string
    slug: string
    title: string
    est_minutes: number
    xp: number
    brief_md: string
    diagram: string | null
    specimen: string | null
    source_refs: string
    module_slug: string
    module_title: string
    zone: string
    floor: number
    color: string
  }
  objectives: string[]
  questions: { id: string; kind: string; prompt: string; choices: { id: string; text: string }[] }[]
  lab: { id: string; title: string; instructions_md: string; xp: number } | null
  progress: { status: string; completed_at: number | null } | null
  prev: { slug: string; title: string } | null
  next: { slug: string; title: string } | null
}

export default function LessonView() {
  const { moduleSlug, lessonSlug } = useParams()
  const { user, game, setGame } = useAuth()
  const [data, setData] = useState<LessonPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [completing, setCompleting] = useState(false)
  const [justEarned, setJustEarned] = useState<number | null>(null)
  const openedAt = useRef(Date.now())

  useEffect(() => {
    openedAt.current = Date.now()
    setData(null)
    setError(null)
    setJustEarned(null)

    const controller = new AbortController()
    api<LessonPayload>(`/lessons/${moduleSlug}/${lessonSlug}`, { signal: controller.signal })
      .then((payload) => {
        setData(payload)
        if (user) void api('/progress/start', { method: 'POST', body: { lessonId: payload.lesson.id } })
      })
      .catch((e: unknown) => {
        if ((e as Error).name !== 'AbortError') {
          setError(e instanceof ApiError ? e.message : 'Could not load that lesson.')
        }
      })
    return () => controller.abort()
  }, [moduleSlug, lessonSlug, user])

  async function complete() {
    if (!data) return
    setCompleting(true)
    try {
      const res = await api<{
        xpGranted: number
        alreadyCompleted: boolean
        game: { totalXp: number; level: number; xpIntoLevel: number; xpForNextLevel: number; progress: number; streak: number; longestStreak: number }
      }>('/progress/complete', {
        method: 'POST',
        body: {
          lessonId: data.lesson.id,
          secondsSpent: Math.round((Date.now() - openedAt.current) / 1000),
        },
      })
      setJustEarned(res.xpGranted)
      setData({ ...data, progress: { status: 'completed', completed_at: Date.now() } })
      if (game) setGame({ ...game, ...res.game })
    } finally {
      setCompleting(false)
    }
  }

  if (error) return <div className="error-panel">{error}</div>
  if (!data) return <div className="loading">Loading lesson…</div>

  const { lesson } = data
  const done = data.progress?.status === 'completed'

  return (
    <article className="lesson">
      <nav className="breadcrumb">
        <Link to="/">Tower</Link>
        <span>/</span>
        <Link to={`/floor/${lesson.module_slug}`}>
          Floor {lesson.floor} · {lesson.zone}
        </Link>
      </nav>

      <header className="lesson-header" style={{ borderColor: lesson.color }}>
        <h1>{lesson.title}</h1>
        <p className="dim">
          {lesson.est_minutes} min · {lesson.xp} XP
          {done && <span className="done-badge"> ✓ completed</span>}
        </p>
      </header>

      {data.objectives.length > 0 && (
        <section className="objectives">
          <h3>After this you will be able to</h3>
          <ul>
            {data.objectives.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="zone zone-brief">
        <h3 className="zone-heading">
          <span className="zone-num">1</span> Brief
        </h3>
        <Markdown source={lesson.brief_md} />
      </section>

      {lesson.diagram && (
        <section className="zone zone-diagram">
          <h3 className="zone-heading">
            <span className="zone-num">2</span> Diagram
          </h3>
          <pre className="diagram">{lesson.diagram}</pre>
        </section>
      )}

      <Specimen id={lesson.specimen} />

      <section className="zone zone-hood">
        <h3 className="zone-heading">
          <span className="zone-num">4</span> Under the hood
        </h3>
        <SourceRefs refs={lesson.source_refs} />
        <TrafficPanel title="Real traffic from this page" />
      </section>

      <Quiz lessonId={lesson.id} questions={data.questions} />

      {data.lab && (
        <section className="zone zone-lab">
          <h3 className="zone-heading">
            <span className="zone-num">6</span> Lab
            <small className="dim"> — {data.lab.xp} XP</small>
          </h3>
          <h4>{data.lab.title}</h4>
          <Markdown source={data.lab.instructions_md} />
          {user && <NoteEditor lessonId={lesson.id} />}
        </section>
      )}

      {user && !data.lab && <NoteEditor lessonId={lesson.id} />}

      <div className="lesson-footer">
        {user ? (
          <button
            type="button"
            className="button button-primary"
            onClick={() => void complete()}
            disabled={completing || done}
          >
            {done ? '✓ Completed' : completing ? 'Saving…' : `Mark complete (+${lesson.xp} XP)`}
          </button>
        ) : (
          <Link to="/login" className="button button-primary">
            Sign in to save your progress
          </Link>
        )}
        {justEarned !== null && justEarned > 0 && <span className="xp-pop">+{justEarned} XP</span>}
        {justEarned === 0 && <span className="dim">Already counted — no double XP.</span>}
      </div>

      <nav className="lesson-nav">
        {data.prev ? (
          <Link to={`/floor/${moduleSlug}/${data.prev.slug}`}>← {data.prev.title}</Link>
        ) : (
          <span />
        )}
        {data.next && <Link to={`/floor/${moduleSlug}/${data.next.slug}`}>{data.next.title} →</Link>}
      </nav>
    </article>
  )
}

/** Zone 4a: pointers at the real files in this repo that implement the lesson. */
function SourceRefs({ refs }: { refs: string }) {
  let parsed: { path: string; label: string }[] = []
  try {
    parsed = JSON.parse(refs)
  } catch {
    return null
  }
  if (parsed.length === 0) return null

  return (
    <div className="source-refs">
      <p className="dim">The code that actually does this, in this repository:</p>
      <ul>
        {parsed.map((r) => (
          <li key={r.path}>
            <code>{r.path}</code> — {r.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
